import React, { useContext } from 'react';
import classNames from 'classnames';
import { Field, FormSpy, useForm, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import validations from 'validations';
import HintMessage from 'components/HintMessage';
import RadioSliderField from 'components/RadioSliderField';
import RadioGroupField from 'components/RadioGroupField';
import useMatchMedia from 'hooks/useMatchMedia';
import useOperationStepValues from 'router/data/useOperationStepValues';
import { WhenTwoFieldChanges } from 'router/subrouters/ListingCreationWizard/components/WhenTwoFieldChanges';
import {
  calculatePriceArraySum,
  calculatePriceDifference,
  calculatePriceDivision,
} from 'router/subrouters/ListingCreationWizard/utils';
import { Listing, PriceDescription } from 'models/Listing';
import useCallbackRef from 'hooks/useCallbackRef';
import { useLocation } from 'react-router-dom';
import useHeightAnimation from 'hooks/useHeightAnimation';
import { AdjustmentsArray } from '../../components/AdjustmentsArray';
import {
  ASKING_PRICE_SELECT,
  AskingPriceSelect,
  STOCK_AT_VALUE,
  StockAtValue,
} from 'models/DealRoom';
import { useGenerateSliderOptions } from '../../hooks/useGenerateSliderOptions';
import ControlledInformationCard from 'components/ControlledInformationCard';
import { OwnerOfBusiness } from 'router/subrouters/ListingCreationWizard/components/OwnerOfBusiness';
import { EnableTeaser } from 'router/subrouters/ListingCreationWizard/components/EnableTeaser';
import PatchListingContext from '../../providers/PatchListing/PatchListing.context';
import { EDIT_ROUTE, REQUIRED_VISIBLE_INFO } from 'data/global';
import ListingFieldRequiredVisibleInfo from '../../components/ListingFieldRequiredVisibleInfo';
import useListingFieldIsRequired from '../../hooks/useListingFieldIsRequired';
import { getFieldValidations } from 'validations/general';
import useFirstRender from 'hooks/useFirstRender';

import './StatementOfInformation.styles.scss';
import './StatementOfInformation.styles.responsive.scss';

type StatementOfInformationProps = {
  className?: string;
};

const StatementOfInformation: React.FC<StatementOfInformationProps> = (
  props,
) => {
  const { className } = props;
  const { t } = useTranslation();
  const {
    descriptionPriceOptions,
    accountingPeriodOptions,
    stockAtValueOptions,
  } = useGenerateSliderOptions();
  const { priceGuideOptions } = useOperationStepValues();

  const { patchListingValue } = useContext(PatchListingContext);

  const classes = classNames('statement-of-information', 'lcw-page', className);
  const [content, contentRef] = useCallbackRef<HTMLDivElement>();

  const isMobile = useMatchMedia('isMobile');
  const isEditing = useLocation().pathname.includes(EDIT_ROUTE);

  const { change } = useForm();
  const { values: listing } = useFormState<Listing>();
  const { askingPriceSelect, stockAtValue } = listing;

  const isFixedPrice = askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE;
  const hasStockAtValue = stockAtValue !== STOCK_AT_VALUE.NO_STOCK_AT_VALUE;

  const isFirstRender = useFirstRender();

  useHeightAnimation({
    element: content,
    defaultOpen: true,
  });

  const [isAmountFromRequired, isAmountFromDefaultRequired] =
    useListingFieldIsRequired('amountFrom');
  const [isAmountToRequired, isAmountToDefaultRequired] =
    useListingFieldIsRequired('amountTo');
  const [isAmountRequired, isAmountDefaultRequired] =
    useListingFieldIsRequired('amount');
  const [isRevenueRequired, isRevenueDefaultRequired] =
    useListingFieldIsRequired('revenue');
  const [isCOGSRequired, isCOGSDefaultRequired] =
    useListingFieldIsRequired('COGS');
  const [isLessOverheadsRequired, isLessOverheadsDefaultRequired] =
    useListingFieldIsRequired('lessOverheads');
  const [isEBITDARequired, isEBITDADefaultRequired] =
    useListingFieldIsRequired('EBITDA');
  const [
    isDepreciationAndAmortisationRequired,
    isDepreciationAndAmortisationDefaultRequired,
  ] = useListingFieldIsRequired('depreciationAndAmortisation');
  const [isEBITRequired, isEBITDefaultRequired] =
    useListingFieldIsRequired('EBIT');
  const [isAccountingPeriodRequired, isAccountingPeriodDefaultRequired] =
    useListingFieldIsRequired('accountingPeriod');
  const [isAverageStockValueRequired, isAverageStockValueDefaultRequired] =
    useListingFieldIsRequired('averageStockValue');

  return (
    <div className={classes}>
      <p className="lcw-page__heading__title">{t('statementOfInformation')}</p>

      <HintMessage
        message={
          <div className="statement-of-information__hint-message">
            <p>
              {t('noDetailsOnTheStatementOfInformationIdentifyTheBusiness')}
            </p>
            <p>{t('soiCriteriaMessage')}</p>
            <ul>
              <li>{t('areLoggedIn')}</li>
              <li>{t('haveVerifiedTherAccount')}</li>
              <li>{t('haveEnquiredOnTheListing')}</li>
            </ul>
            <p>
              {t(
                'priceInformationEnablesResolveToMatchListingsToPriceFilteredSearch',
              )}
            </p>
          </div>
        }
      />

      <div
        className={classNames(
          'white-panel',
          'statement-of-information__price-guide',
        )}
      >
        <p className="statement-of-information__title">{t('priceGuide')}</p>
        <div
          className={classNames(
            'lcw-page__field-row',
            'statement-of-information__sale-price',
          )}
        >
          {!isFixedPrice ? (
            <div className="lcw-page__field-column">
              <Field
                name="amountFrom"
                label={t('amountFrom')}
                labelChildren={
                  <ListingFieldRequiredVisibleInfo
                    fieldName={`amountFrom${REQUIRED_VISIBLE_INFO}`}
                    fieldLabel={t('amountFrom')}
                    isDefaultRequired={isAmountFromDefaultRequired}
                  />
                }
                numberStyle="dottedNumber"
                placeholder="$"
                component={InputField}
                customOnBlur={(
                  e: React.FocusEvent<HTMLInputElement>,
                  isValid: boolean,
                ) => {
                  if (isValid)
                    patchListingValue({ amountFrom: e.target.value });
                }}
                {...getFieldValidations(isAmountFromRequired, [
                  validations.positiveNumbersOnly(),
                ])}
              />
              <Field
                name="amountTo"
                label={t('amountTo')}
                labelChildren={
                  <ListingFieldRequiredVisibleInfo
                    fieldName={`amountTo${REQUIRED_VISIBLE_INFO}`}
                    fieldLabel={t('amountTo')}
                    isDefaultRequired={isAmountToDefaultRequired}
                  />
                }
                numberStyle="dottedNumber"
                placeholder="$"
                component={InputField}
                customOnBlur={(
                  e: React.FocusEvent<HTMLInputElement>,
                  isValid: boolean,
                ) => {
                  if (isValid) patchListingValue({ amountTo: e.target.value });
                }}
                {...getFieldValidations(isAmountToRequired, [
                  validations.positiveNumbersOnly(),
                  validations.isGreatherOrEqualThanField('amountFrom'),
                ])}
              />

              {askingPriceSelect ===
                ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST && (
                <p className="eoi-description">
                  {t('askingPriceEoiDescription')}
                </p>
              )}
            </div>
          ) : (
            <Field
              name="amount"
              label={t('salePrice')}
              labelChildren={
                <ListingFieldRequiredVisibleInfo
                  fieldName={`amount${REQUIRED_VISIBLE_INFO}`}
                  fieldLabel={t('salePrice')}
                  isDefaultRequired={isAmountDefaultRequired}
                />
              }
              numberStyle="dottedNumber"
              placeholder="$"
              component={InputField}
              customOnBlur={(
                e: React.FocusEvent<HTMLInputElement>,
                isValid: boolean,
              ) => {
                if (isValid) patchListingValue({ amount: e.target.value });
              }}
              {...getFieldValidations(isAmountRequired, [
                validations.greatherThanZero(
                  t('onlyPositiveNumbersAreAllowed'),
                ),
              ])}
            />
          )}

          <Field
            name="askingPriceSelect"
            slideSteps={priceGuideOptions}
            component={RadioSliderField}
            customOnChange={(askingPriceSelect: AskingPriceSelect) =>
              patchListingValue(
                {
                  askingPriceSelect,
                },
                { amount: null, amountFrom: null, amountTo: null },
              )
            }
          />
        </div>
        <div>
          <p className="statement-of-information__title">
            {t('financialStatement')}
          </p>
          <p className="statement-of-information__description">
            {t('financialStatementDescription')}
          </p>
        </div>
        <Field
          name="revenue"
          className="statement-of-information__row-input-field"
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          label={t('revenueUC')}
          labelChildren={
            <ListingFieldRequiredVisibleInfo
              fieldName={`revenue${REQUIRED_VISIBLE_INFO}`}
              fieldLabel={t('revenueUC')}
              isDefaultRequired={isRevenueDefaultRequired}
            />
          }
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) patchListingValue({ revenue: e.target.value });
          }}
          {...getFieldValidations(isRevenueRequired, [
            validations.positiveNumbersOnly(),
            validations.isGreatherOrEqualThanField('COGS'),
          ])}
        />
        <Field
          name="COGS"
          label={t('cogs')}
          labelChildren={
            <ListingFieldRequiredVisibleInfo
              fieldName={`COGS${REQUIRED_VISIBLE_INFO}`}
              fieldLabel={t('cogs')}
              isDefaultRequired={isCOGSDefaultRequired}
            />
          }
          className="statement-of-information__row-input-field"
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) patchListingValue({ COGS: e.target.value });
          }}
          {...getFieldValidations(isCOGSRequired, [
            validations.positiveNumbersOnly(),
          ])}
        />
        <span className="lcw-page__field-border" />
        <WhenTwoFieldChanges
          name="grossProfit"
          nameFrom="revenue"
          nameTo="COGS"
          calculateNewValue={calculatePriceDifference}
        />
        <Field
          name="grossProfit"
          numberStyle="dottedNumber"
          placeholder="$"
          label={t('grossProfit')}
          className="statement-of-information__row-input-field"
          readOnly
          component={InputField}
          validate={validations.positiveNumbersOnly()}
        />
        <span className="lcw-page__field-border" />
        <Field
          name="lessOverheads"
          numberStyle="dottedNumber"
          placeholder="$"
          label={t('operatingExpenses')}
          labelChildren={
            <ListingFieldRequiredVisibleInfo
              fieldName={`lessOverheads${REQUIRED_VISIBLE_INFO}`}
              fieldLabel={t('operatingExpenses')}
              isDefaultRequired={isLessOverheadsDefaultRequired}
            />
          }
          className="statement-of-information__row-input-field"
          component={InputField}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) patchListingValue({ lessOverheads: e.target.value });
          }}
          {...getFieldValidations(isLessOverheadsRequired, [
            validations.positiveNumbersOnly(),
            validations.isLowerThanField('grossProfit'),
          ])}
        />
        <span className="lcw-page__field-border" />
        <WhenTwoFieldChanges
          name="operatingProfit"
          nameFrom="grossProfit"
          nameTo="lessOverheads"
          calculateNewValue={calculatePriceDifference}
        />
        <Field
          name="operatingProfit"
          numberStyle="dottedNumber"
          placeholder="$"
          label={t('operatingProfitUC')}
          className="statement-of-information__row-input-field"
          readOnly
          component={InputField}
          validate={validations.positiveNumbersOnly()}
        />
        <span className="lcw-page__field-border" />
        <Field
          name="EBITDA"
          numberStyle="dottedNumber"
          placeholder="$"
          className="statement-of-information__row-input-field"
          label={t('ebitda')}
          labelChildren={
            <ListingFieldRequiredVisibleInfo
              fieldName={`EBITDA${REQUIRED_VISIBLE_INFO}`}
              fieldLabel={t('ebitda')}
              isDefaultRequired={isEBITDADefaultRequired}
            />
          }
          component={InputField}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) patchListingValue({ EBITDA: e.target.value });
          }}
          {...getFieldValidations(isEBITDARequired, [
            validations.validDecimalNumbersOnlyValidation(
              t('numbersOnlyError'),
            ),
          ])}
        />
        <Field
          name="depreciationAndAmortisation"
          numberStyle="dottedNumber"
          className="statement-of-information__row-input-field"
          label={t('depreciationAndAmortisation')}
          labelChildren={
            <ListingFieldRequiredVisibleInfo
              fieldName={`depreciationAndAmortisation${REQUIRED_VISIBLE_INFO}`}
              fieldLabel={t('depreciationAndAmortisation')}
              isDefaultRequired={isDepreciationAndAmortisationDefaultRequired}
            />
          }
          placeholder="$"
          component={InputField}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid)
              patchListingValue({
                depreciationAndAmortisation: e.target.value,
              });
          }}
          {...getFieldValidations(isDepreciationAndAmortisationRequired, [
            validations.positiveNumbersOnly(),
          ])}
        />
        <Field
          name="EBIT"
          className="statement-of-information__row-input-field"
          label={t('ebit')}
          labelChildren={
            <ListingFieldRequiredVisibleInfo
              fieldName={`EBIT${REQUIRED_VISIBLE_INFO}`}
              fieldLabel={t('ebit')}
              isDefaultRequired={isEBITDefaultRequired}
            />
          }
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) patchListingValue({ EBIT: e.target.value });
          }}
          {...getFieldValidations(isEBITRequired, [
            validations.positiveNumbersOnly(),
          ])}
        />
        <WhenTwoFieldChanges
          name="EBITDAMultiple"
          nameFrom={isFixedPrice ? 'amount' : 'amountFrom'}
          nameTo="EBITDA"
          calculateNewValue={calculatePriceDivision}
        />
        <Field
          name="EBITDAMultiple"
          numberStyle="dottedNumber"
          label={t('ebitdaMultiple')}
          className="statement-of-information__row-input-field"
          placeholder="$"
          readOnly
          component={InputField}
          validate={validations.validDecimalNumbersOnlyValidation(
            t('numbersOnlyError'),
          )}
        />
        <span className="lcw-page__field-border" />
        <AdjustmentsArray />
        <span className="lcw-page__field-border" />
        <FormSpy
          subscription={{ values: true }}
          onChange={({ values }) => {
            if (isFirstRender) return;

            const valueArray = values.adjustments?.map(
              (el: { netAmount: string }) => el?.netAmount,
            );
            const oldValue = values.totalAdjustments;
            const newValue = calculatePriceArraySum(valueArray) || 0;

            const isChangedValue = oldValue !== newValue;

            if (valueArray?.length && isChangedValue) {
              change('totalAdjustments', newValue);
            }
          }}
        />
        <Field
          name="totalAdjustments"
          label={t('totalAdjustmentsUC')}
          className="statement-of-information__row-input-field"
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          readOnly
        />
        <span className="lcw-page__field-border" />
        <div className="lcw-page__field-row">
          <Field
            name="priceDescription"
            label={t('description')}
            slideSteps={descriptionPriceOptions}
            component={RadioSliderField}
            customOnChange={(priceDescription: PriceDescription) =>
              patchListingValue({ priceDescription })
            }
            {...getFieldValidations(false)}
          />

          <RadioGroupField
            name="accountingPeriod"
            options={accountingPeriodOptions}
            label={t('accountingPeriod')}
            labelChildren={
              <ListingFieldRequiredVisibleInfo
                fieldName={`accountingPeriod${REQUIRED_VISIBLE_INFO}`}
                fieldLabel={t('accountingPeriod')}
                isDefaultRequired={isAccountingPeriodDefaultRequired}
              />
            }
            customOnChange={(accountingPeriod: string) => {
              patchListingValue({ accountingPeriod });
            }}
            {...getFieldValidations(isAccountingPeriodRequired)}
          />
        </div>
        <EnableTeaser listing={listing} isEditing={isEditing} />
      </div>

      <div className="white-panel">
        <div className="lcw-page__field-column">
          <p className="statement-of-information__title">{t('stockAtValue')}</p>

          {isMobile ? (
            <RadioGroupField
              name="stockAtValue"
              options={stockAtValueOptions}
              customOnChange={(stockAtValue: StockAtValue) => {
                patchListingValue({ stockAtValue });
              }}
            />
          ) : (
            <Field
              name="stockAtValue"
              slideSteps={stockAtValueOptions}
              component={RadioSliderField}
              validate={validations.required()}
              customOnChange={(stockAtValue: StockAtValue) =>
                patchListingValue({ stockAtValue })
              }
            />
          )}

          <div
            ref={contentRef}
            className={classNames(
              'lcw-page__field-row',
              'statement-of-information__stock',
            )}
          >
            {hasStockAtValue && (
              <Field
                name="averageStockValue"
                label={t('averageStockValue')}
                labelChildren={
                  <ListingFieldRequiredVisibleInfo
                    fieldName={`averageStockValue${REQUIRED_VISIBLE_INFO}`}
                    fieldLabel={t('averageStockValue')}
                    isDefaultRequired={isAverageStockValueDefaultRequired}
                  />
                }
                numberStyle="dottedNumber"
                component={InputField}
                placeholder="$"
                description={t(
                  'useEitherTheMostRecentFullCalendarYearFinancialYearOrAverageOfLastTwoYears',
                )}
                customOnBlur={(
                  e: React.FocusEvent<HTMLInputElement>,
                  isValid: boolean,
                ) => {
                  if (isValid)
                    patchListingValue({
                      averageStockValue: e.target.value || null,
                    });
                }}
                {...getFieldValidations(isAverageStockValueRequired, [
                  validations.positiveNumbersOnly(),
                ])}
              />
            )}

            <span />
          </div>
        </div>
      </div>

      <OwnerOfBusiness />
      <ControlledInformationCard />
    </div>
  );
};

export default StatementOfInformation;
